/**
 * 平台音频闸门（全局）
 *
 * 解决的问题：用户一边听平台音乐，一边点进作品，平台音乐还在响，跟作品里的
 * 声音叠在一起。要的效果是「进作品就安静，退出作品恢复」。
 *
 * 为什么不用「找到播放器组件、调它的 pause」：
 * 那样每加一个播放器就得回来改一次 GameDetail，迟早漏。
 * 这里改成在源头接管——把所有 new Audio() 造出来的元素登记进注册表，
 * 进入作品时统一暂停，退出时统一恢复。
 * 以后任何人再写播放器，只要用 new Audio()，就自动生效，不用改这里。
 *
 * 注意：不少播放器（含首页音乐）的 Audio 对象不挂到 DOM 上，
 * 所以 document.querySelectorAll('audio') 是抓不到的，必须靠注册表。
 */

const nativeAudio = typeof window !== 'undefined' ? window.Audio : null;

/** 所有被接管过的 Audio 元素 */
const registry = new Set();
/** 本次被我们暂停的元素，退出时按这个名单恢复 */
let pausedSnapshot = [];

function track(el) {
  registry.add(el);
  // 播完或被卸载时移除，避免集合无限增长
  try {
    el.addEventListener('ended', () => registry.delete(el));
    el.addEventListener('emptied', () => registry.delete(el));
  } catch (e) { /* 忽略 */ }
  return el;
}

/* 接管全局 Audio 构造器。
   用 function 而不是 class，这样 new 与直接调用两种写法都能拿到真正的元素。
   prototype 指回原生，保证 instanceof Audio 依然成立。 */
function TrackedAudio(...args) {
  return track(new nativeAudio(...args));
}

if (nativeAudio) {
  TrackedAudio.prototype = nativeAudio.prototype;
  try {
    window.Audio = TrackedAudio;
  } catch (e) {
    console.warn('[platformAudio] 接管 Audio 失败，将退回 DOM 扫描', e);
  }
}

/** 暂停平台侧所有正在播放的音频，返回暂停了几个 */
export function pausePlatformAudio() {
  pausedSnapshot = [];
  const candidates = new Set(registry);
  // 兜底：DOM 里真实存在的 audio（不依赖构造器接管）
  try {
    document.querySelectorAll('audio').forEach((el) => candidates.add(el));
  } catch (e) { /* 忽略 */ }

  candidates.forEach((el) => {
    try {
      if (el && !el.paused) {
        el.pause();
        pausedSnapshot.push(el);
      }
    } catch (e) { /* 单个元素异常不影响其它 */ }
  });
  return pausedSnapshot.length;
}

/** 恢复上一次被暂停的音频 */
export function resumePlatformAudio() {
  const list = pausedSnapshot;
  pausedSnapshot = [];
  list.forEach((el) => {
    try {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (e) { /* 元素已销毁就算了 */ }
  });
  return list.length;
}

/* 只读诊断接口：控制台里能用 __iwAudioStats() 看「接管了几个音频、现在几个在响」，
   方便以后确认「进游戏到底有没有把音乐关掉」，不参与业务逻辑。 */
if (typeof window !== 'undefined') {
  window.__iwAudioStats = () => {
    const list = Array.from(registry);
    return {
      registered: list.length,
      playing: list.filter((el) => el && !el.paused).length,
      pausedByUs: pausedSnapshot.length,
    };
  };
}

